import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface WorkspaceOwner {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url?: string | null;
}

export interface AdminWorkspace {
  id: string;
  name: string | null;
  description: string | null;
  created_at: string;
  updated_at: string | null;
  icon_url: string | null;
  owner: WorkspaceOwner | null;
  credits_balance: number;
  monthly_credits_used: number;
  total_credit: number;
  total_usage: number;
  has_subscription: boolean;
  plan_amount: string | null;
  member_count: number;
  datasource_count: number;
  conversation_count: number;
  artifact_count: number;
}

export interface WorkspaceMember {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
  created_at: string;
  role: string;
}

export interface WorkspaceDetail {
  workspace: {
    id: string;
    name: string | null;
    description: string | null;
    created_at: string;
    updated_at: string | null;
    icon_url: string | null;
    credits_balance: number;
    monthly_credits_used: number;
    total_credit: number;
    total_usage: number;
    usage_period_start: string | null;
    usage_period_end: string | null;
    owner: WorkspaceOwner | null;
    subscription: {
      id: string;
      plan_amount: string | null;
      current_period_start: number | null;
      current_period_end: number | null;
    } | null;
    counts: {
      datasources: number;
      ai_conversations: number;
      artifacts: number;
      topics: number;
      workspace_roles: number;
      workspace_invites: number;
    };
  };
  members: WorkspaceMember[];
  usage: {
    last30Days: {
      totalCost: number;
      totalTokens: number;
      promptTokens: number;
      completionTokens: number;
      requestCount: number;
    };
    tokenUsageOverTime?: {
      labels: string[];
      datasets: Array<{
        label: string;
        data: number[];
        borderColor: string;
        backgroundColor?: string;
        fill?: boolean;
      }>;
    };
    tokensByModelOverTime?: {
      labels: string[];
      datasets: Array<{
        label: string;
        data: number[];
        borderColor: string;
        backgroundColor?: string;
        fill?: boolean;
      }>;
    };
    timeRange?: number;
  };
  recentConversations: Array<{
    id: string;
    created_at: string;
    title: string | null;
    users: {
      email: string;
      first_name: string | null;
      last_name: string | null;
    } | null;
  }>;
}

export interface WorkspaceStats {
  totalWorkspaces: number;
  activeWorkspaces: number;
  totalCreditsBalance: number;
  totalCreditsUsed: number;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminWorkspacesFilters {
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface AdminWorkspacesState {
  workspaces: AdminWorkspace[];
  stats: WorkspaceStats | null;
  selectedWorkspace: WorkspaceDetail | null;
  pagination: Pagination;
  filters: AdminWorkspacesFilters;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  detailStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialFilters: AdminWorkspacesFilters = {
  search: '',
  sortBy: 'created_at',
  sortOrder: 'desc',
};

const initialState: AdminWorkspacesState = {
  workspaces: [],
  stats: null,
  selectedWorkspace: null,
  pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
  filters: initialFilters,
  status: 'idle',
  detailStatus: 'idle',
  error: null,
};

export const fetchAdminWorkspaces = createAsyncThunk(
  'adminWorkspaces/fetchList',
  async (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await fetch(`/api/admin/workspaces?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch workspaces');
    return response.json();
  }
);

export const fetchWorkspaceDetail = createAsyncThunk(
  'adminWorkspaces/fetchDetail',
  async ({ workspaceId, timeRange }: { workspaceId: string; timeRange?: number }) => {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    const response = await fetch(`/api/admin/workspaces/${workspaceId}${params}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Workspace not found');
      throw new Error('Failed to fetch workspace details');
    }
    return response.json();
  }
);

const adminWorkspacesSlice = createSlice({
  name: 'adminWorkspaces',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AdminWorkspacesFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialFilters;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    clearSelectedWorkspace: (state) => {
      state.selectedWorkspace = null;
      state.detailStatus = 'idle';
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAdminWorkspaces.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(fetchAdminWorkspaces.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.workspaces = action.payload.data.workspaces;
      state.pagination = action.payload.data.pagination;
      state.stats = action.payload.data.stats;
    });
    builder.addCase(fetchAdminWorkspaces.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch workspaces';
    });

    builder.addCase(fetchWorkspaceDetail.pending, (state) => {
      state.detailStatus = 'loading';
      state.error = null;
    });
    builder.addCase(fetchWorkspaceDetail.fulfilled, (state, action) => {
      state.detailStatus = 'succeeded';
      state.selectedWorkspace = action.payload.data;
    });
    builder.addCase(fetchWorkspaceDetail.rejected, (state, action) => {
      state.detailStatus = 'failed';
      state.error = action.error.message || 'Failed to fetch workspace details';
    });
  },
});

export const {
  clearError,
  setFilters,
  resetFilters,
  setPage,
  clearSelectedWorkspace,
  resetState,
} = adminWorkspacesSlice.actions;

export default adminWorkspacesSlice.reducer;
